import { useEffect, useRef, useState } from "react";
import type { ConnState } from "../api/realtime";

const SHOW_AFTER_MS = 2000;

/**
 * 연결 상태 띠: 인터넷이 끊겼거나 서버와 연결이 2초 넘게 끊기면 위에 띄우고,
 * 다시 붙으면 "다시 연결했습니다"를 잠깐 보여준다. 잠깐 끊겼다 바로 붙으면 띄우지 않는다.
 */
export function ConnectionBanner({ state, reconnect }: { state: ConnState; reconnect: () => void }) {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [shown, setShown] = useState<"down" | "back" | null>(null);
  const wasDown = useRef(false);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const down = !online || state === "closed";
  useEffect(() => {
    if (down) {
      const t = setTimeout(() => {
        wasDown.current = true;
        setShown("down");
      }, online ? SHOW_AFTER_MS : 0);
      return () => clearTimeout(t);
    }
    if (state === "open" && wasDown.current) {
      wasDown.current = false;
      setShown("back");
      const t = setTimeout(() => setShown(null), 2500);
      return () => clearTimeout(t);
    }
    if (state === "open") setShown(null);
  }, [down, online, state]);

  if (!shown) return null;
  if (shown === "back")
    return (
      <div className="conn-banner back" role="status">
        다시 연결했습니다. 그동안의 변경을 받았습니다.
      </div>
    );
  return (
    <div className="conn-banner" role="status">
      <span>{online ? "서버와 연결이 끊겼습니다. 다시 연결하는 중…" : "인터넷에 연결되어 있지 않습니다. 연결되면 바로 이어서 받습니다."}</span>
      {online && (
        <button type="button" className="btn btn-sm btn-quiet" onClick={reconnect}>
          지금 다시 연결
        </button>
      )}
    </div>
  );
}
